import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinQueue, joinCasualQueue, leaveQueue, getQueueStatus, getMaps, acceptMatch, declineMatch } from '../api/api';

export default function Queue() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [subMode, setSubMode] = useState(null);
  const [inQueue, setInQueue] = useState(false);
  const [queueTime, setQueueTime] = useState(0);
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [matchFound, setMatchFound] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    getMaps('wingman').then(res => setMaps(res.data.maps || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let interval;
    if (inQueue) interval = setInterval(() => setQueueTime(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [inQueue]);

  useEffect(() => {
    let interval;
    if (inQueue) {
      interval = setInterval(async () => {
        try {
          const res = await getQueueStatus();
          if (res.data.status === 'match_found') {
            setMatchFound(res.data);
            setInQueue(false);
          }
        } catch {}
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [inQueue]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleJoinQueue = async () => {
    setLoading(true);
    setError('');
    try {
      if (subMode === 'casual') {
        if (!selectedMap) { setError('Please select a map'); setLoading(false); return; }
        await joinCasualQueue(selectedMap);
      } else {
        await joinQueue('competitive');
      }
      setInQueue(true);
      setQueueTime(0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join queue');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveQueue = async () => {
    try { await leaveQueue(); setInQueue(false); setQueueTime(0); } catch {}
  };

  const handleAccept = async () => {
    try { await acceptMatch(matchFound.match_id); navigate(`/match/${matchFound.match_id}`); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to accept'); }
  };

  const handleDecline = async () => {
    try { await declineMatch(matchFound.match_id); setMatchFound(null); } catch {}
  };

  const mapImages = {
    'de_cbble': '/assets/maps/cobblestone.jpg',
    'de_inferno': '/assets/maps/inferno.jpg',
    'de_lake': '/assets/maps/lake.jpg',
    'de_overpass': '/assets/maps/overpass.jpg',
    'de_shortdust': '/assets/maps/shortdust.jpg',
    'de_shortnuke': '/assets/maps/nuke.jpg',
    'de_train': '/assets/maps/train.jpg',
    'de_vertigo': '/assets/maps/vertigo.jpg',
  };

  return (
    <div style={s.root}>

      {/* MATCH FOUND OVERLAY */}
      {matchFound && (
        <div style={s.overlay}>
          <div style={s.matchFoundBox}>
            <div style={s.matchFoundGlow} />
            <div style={s.matchFoundEyebrow}>
              <span style={s.eyebrowDot} />
              MATCH FOUND
            </div>
            <div style={s.matchFoundTitle}>MATCH FOUND</div>
            <div style={s.matchFoundMap}>{matchFound.map || 'Map TBD'}</div>
            <div style={s.matchFoundButtons}>
              <button style={s.acceptBtn} onClick={handleAccept}>ACCEPT</button>
              <button style={s.declineBtn} onClick={handleDecline}>DECLINE</button>
            </div>
          </div>
        </div>
      )}

      {/* WINGMAN SUBMODE OVERLAY */}
      {mode === 'wingman' && !subMode && !inQueue && (
        <div style={s.overlay}>
          <div style={s.submodeBox}>
            <div style={s.submodeGlow} />
            <button style={s.submodeClose} onClick={() => setMode(null)}>✕</button>
            <div style={s.submodeEyebrow}>
              <span style={s.eyebrowDot} />
              WINGMAN
            </div>
            <div style={s.submodeTitle}>Select Mode</div>
            <div style={s.submodeOptions}>
              <button
                style={{
                  ...s.submodeBtn,
                  borderColor: hoveredItem === 'comp' ? '#E8192C60' : '#1e1e1e',
                  boxShadow: hoveredItem === 'comp' ? '0 0 24px #E8192C20' : 'none',
                  background: hoveredItem === 'comp' ? '#E8192C08' : '#0a0a0a',
                }}
                onMouseEnter={() => setHoveredItem('comp')}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => setSubMode('competitive')}
              >
                <div style={s.submodeBtnTop}>
                  <div style={s.submodeBtnName}>COMPETITIVE</div>
                  <div style={{...s.submodeBtnTag, color:'#E8192C', borderColor:'#E8192C40'}}>RANKED</div>
                </div>
                <div style={s.submodeBtnDesc}>ELO ranked · Map veto · Coin flip · 128 tick</div>
              </button>
              <button
                style={{
                  ...s.submodeBtn,
                  borderColor: hoveredItem === 'cas' ? '#FF6B2B60' : '#1e1e1e',
                  boxShadow: hoveredItem === 'cas' ? '0 0 24px #FF6B2B20' : 'none',
                  background: hoveredItem === 'cas' ? '#FF6B2B08' : '#0a0a0a',
                }}
                onMouseEnter={() => setHoveredItem('cas')}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => setSubMode('casual')}
              >
                <div style={s.submodeBtnTop}>
                  <div style={s.submodeBtnName}>CASUAL</div>
                  <div style={{...s.submodeBtnTag, color:'#FF6B2B', borderColor:'#FF6B2B40'}}>UNRANKED</div>
                </div>
                <div style={s.submodeBtnDesc}>No ELO · Pick your map · Relaxed play</div>
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={s.content}>

        {/* HEADER */}
        <div style={s.header}>
          <div style={s.headerEyebrow}>
            <span style={s.eyebrowDot} />
            MATCHMAKING
          </div>
          <div style={s.headerTitle}>Find a Match</div>
          <div style={s.headerSub}>Choose your game mode and queue up</div>
        </div>

        {/* MAIN MODE SELECT */}
        {!inQueue && !subMode && (
          <div style={s.mainModeGrid}>

            {/* WINGMAN */}
            <button
              style={{
                ...s.mainModeCard,
                borderColor: hoveredItem === 'wingman' ? '#E8192C60' : '#E8192C20',
                boxShadow: hoveredItem === 'wingman' ? '0 0 32px #E8192C15' : 'none',
                background: hoveredItem === 'wingman' ? '#E8192C06' : '#111111',
              }}
              onMouseEnter={() => setHoveredItem('wingman')}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={() => setMode('wingman')}
            >
              <div style={s.mainModeTop}>
                <div style={s.mainModeIcon}>◈</div>
                <div style={{...s.mainModeTag, color:'#E8192C', borderColor:'#E8192C30'}}>2V2</div>
              </div>
              <div style={s.mainModeName}>WINGMAN</div>
              <div style={s.mainModeDesc}>Competitive or casual wingman matchmaking on CS:GO Legacy servers.</div>
              <div style={s.mainModeArrow}>PLAY →</div>
            </button>

            {/* 5V5 */}
            <button
              style={{
                ...s.mainModeCard,
                opacity: 0.4,
                cursor: 'not-allowed',
                borderColor: '#FF6B2B20',
              }}
            >
              <div style={s.mainModeTop}>
                <div style={s.mainModeIcon}>◆</div>
                <div style={{...s.mainModeTag, color:'#FF6B2B', borderColor:'#FF6B2B30'}}>5V5</div>
              </div>
              <div style={s.mainModeName}>5V5</div>
              <div style={s.mainModeDesc}>Full 5v5 competitive matchmaking. Coming soon to Repeaked.</div>
              <div style={{...s.mainModeArrow, color:'#444'}}>COMING SOON</div>
            </button>

          </div>
        )}

        {/* COMPETITIVE QUEUE */}
        {!inQueue && subMode === 'competitive' && (
          <div style={s.queueBox}>
            <button style={s.backBtn} onClick={() => { setSubMode(null); setMode(null); }}>← BACK</button>
            <div style={s.queueEyebrow}>
              <span style={s.eyebrowDot} />
              WINGMAN COMPETITIVE
            </div>
            <div style={s.queueTitle}>Ready to queue?</div>
            <div style={s.queueDesc}>All 8 wingman maps in pool. Map decided by veto after match is found.</div>
            <div style={s.mapGrid}>
              {maps.map(m => (
                <div key={m.map_name} style={s.mapCard}>
                  <div style={s.mapCardImg}>
                    <img
                      src={mapImages[m.map_name]}
                      alt={m.display_name}
                      style={s.mapImg}
                      onError={e => e.target.style.display = 'none'}
                    />
                  </div>
                  <div style={{...s.mapCardName, color:'#555'}}>{m.display_name}</div>
                </div>
              ))}
            </div>
            {error && <div style={s.error}>{error}</div>}
            <button style={s.queueBtn} onClick={handleJoinQueue} disabled={loading}>
              {loading ? 'JOINING...' : '▶ ENTER QUEUE'}
            </button>
          </div>
        )}

        {/* CASUAL QUEUE */}
        {!inQueue && subMode === 'casual' && (
          <div style={s.queueBox}>
            <button style={s.backBtn} onClick={() => { setSubMode(null); setMode(null); }}>← BACK</button>
            <div style={s.queueEyebrow}>
              <span style={s.eyebrowDot} />
              WINGMAN CASUAL
            </div>
            <div style={s.queueTitle}>Select a map</div>
            <div style={s.queueDesc}>Pick the map you want to play on.</div>
            <div style={s.mapGrid}>
              {maps.map(m => (
                <div key={m.map_name} style={{
                  ...s.mapCard,
                  outline: selectedMap === m.map_name ? '2px solid #FF6B2B' : '2px solid transparent',
                }} onClick={() => setSelectedMap(m.map_name)}>
                  <div style={s.mapCardImg}>
                    <img
                      src={mapImages[m.map_name]}
                      alt={m.display_name}
                      style={s.mapImg}
                      onError={e => e.target.style.display = 'none'}
                    />
                    {selectedMap === m.map_name && (
                      <div style={s.mapSelected}>✓</div>
                    )}
                  </div>
                  <div style={{...s.mapCardName, color: selectedMap === m.map_name ? '#FF6B2B' : '#555'}}>
                    {m.display_name}
                  </div>
                </div>
              ))}
            </div>
            {error && <div style={s.error}>{error}</div>}
            <button style={s.queueBtn} onClick={handleJoinQueue} disabled={loading}>
              {loading ? 'JOINING...' : '▶ ENTER QUEUE'}
            </button>
          </div>
        )}

        {/* IN QUEUE */}
        {inQueue && (
          <div style={s.inQueueWrap}>
            <div style={s.inQueueGlow} />
            <div style={s.inQueueEyebrow}>
              <span style={{...s.eyebrowDot, background:'#4CAF50', boxShadow:'0 0 6px #4CAF50'}} />
              SEARCHING
            </div>
            <div style={s.inQueueTimer}>{formatTime(queueTime)}</div>
            <div style={s.inQueueLabel}>SEARCHING FOR MATCH</div>
            <div style={s.inQueueMode}>
              {subMode === 'competitive' ? 'Wingman Competitive' : `Wingman Casual · ${selectedMap}`}
            </div>
            <div style={s.dots}>
              <div style={s.dot}/>
              <div style={{...s.dot, animationDelay:'0.3s'}}/>
              <div style={{...s.dot, animationDelay:'0.6s'}}/>
            </div>
            <button style={s.leaveBtn} onClick={handleLeaveQueue}>LEAVE QUEUE</button>
          </div>
        )}

      </div>

      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: 0.2; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes rp-page-up{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @media (prefers-reduced-motion: reduce){*{animation-duration:1ms!important;transition-duration:1ms!important}}
      `}</style>
    </div>
  );
}

const s = {
  root: { background:'#0a0a0a',  color:'#F0F2F5', fontFamily:"'Rajdhani', 'Segoe UI', sans-serif", position:'relative', animation:'rp-page-up 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both' },

  content: { maxWidth:'1000px', margin:'0 auto', padding:'32px' },

  header: { marginBottom:'48px' },
  headerEyebrow: { display:'flex', alignItems:'center', gap:'8px', fontSize:'10px', letterSpacing:'4px', color:'#E8192C', fontWeight:'700', marginBottom:'8px' },
  headerTitle: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'clamp(40px, 8vw, 64px)', letterSpacing:'6px', color:'#F0F2F5', lineHeight:'1' },
  headerSub: { fontSize:'14px', color:'#444', letterSpacing:'1px', marginTop:'8px' },
  eyebrowDot: { width:'5px', height:'5px', borderRadius:'50%', background:'#E8192C', boxShadow:'0 0 6px #E8192C', display:'inline-block', flexShrink:0 },

  // MAIN MODE GRID
  mainModeGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', maxWidth:'700px' },
  mainModeCard: { background:'#111111', border:'1px solid', borderRadius:'8px', padding:'32px 28px', cursor:'pointer', textAlign:'left', transition:'all 0.2s', display:'flex', flexDirection:'column', gap:'12px' },
  mainModeTop: { display:'flex', alignItems:'center', justifyContent:'space-between' },
  mainModeIcon: { fontSize:'24px', color:'#E8192C' },
  mainModeTag: { fontSize:'9px', letterSpacing:'2px', fontWeight:'700', border:'1px solid', borderRadius:'2px', padding:'3px 10px' },
  mainModeName: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'32px', letterSpacing:'4px', color:'#F0F2F5', lineHeight:'1' },
  mainModeDesc: { fontSize:'12px', color:'#444', lineHeight:'1.6' },
  mainModeArrow: { fontSize:'12px', letterSpacing:'3px', color:'#E8192C', fontWeight:'700', fontFamily:"'Bebas Neue', sans-serif", marginTop:'8px' },

  // SUBMODE OVERLAY
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200 },
  submodeBox: { background:'#111111', border:'1px solid #1e1e1e', borderRadius:'8px', padding:'48px', display:'flex', flexDirection:'column', gap:'24px', minWidth:'480px', maxWidth:'560px', position:'relative', overflow:'hidden' },
  submodeGlow: { position:'absolute', top:'-40px', left:'50%', transform:'translateX(-50%)', width:'300px', height:'200px', background:'radial-gradient(ellipse, rgba(232,25,44,0.1) 0%, transparent 70%)', pointerEvents:'none' },
  submodeClose: { position:'absolute', top:'16px', right:'16px', background:'transparent', border:'none', color:'#333', fontSize:'16px', cursor:'pointer' },
  submodeEyebrow: { display:'flex', alignItems:'center', gap:'8px', fontSize:'10px', letterSpacing:'4px', color:'#E8192C', fontWeight:'700' },
  submodeTitle: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'36px', letterSpacing:'5px', color:'#F0F2F5', marginTop:'-8px' },
  submodeOptions: { display:'flex', flexDirection:'column', gap:'12px' },
  submodeBtn: { background:'#0a0a0a', border:'1px solid #1e1e1e', borderRadius:'6px', padding:'20px 24px', cursor:'pointer', textAlign:'left', transition:'all 0.2s', width:'100%' },
  submodeBtnTop: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' },
  submodeBtnName: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'22px', letterSpacing:'3px', color:'#F0F2F5' },
  submodeBtnTag: { fontSize:'9px', letterSpacing:'2px', fontWeight:'700', border:'1px solid', borderRadius:'2px', padding:'3px 10px' },
  submodeBtnDesc: { fontSize:'12px', color:'#444', letterSpacing:'1px', lineHeight:'1.5' },

  // QUEUE BOX
  queueBox: { background:'#111111', border:'1px solid #1e1e1e', borderRadius:'8px', padding:'40px', display:'flex', flexDirection:'column', gap:'20px', maxWidth:'1100px', margin:'0 auto', width:'100%' },
  backBtn: { background:'transparent', border:'none', color:'#333', fontSize:'11px', letterSpacing:'2px', cursor:'pointer', textAlign:'left', padding:0, fontFamily:"'Bebas Neue', sans-serif" },
  queueEyebrow: { display:'flex', alignItems:'center', gap:'8px', fontSize:'10px', letterSpacing:'4px', color:'#E8192C', fontWeight:'700' },
  queueTitle: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'32px', letterSpacing:'4px', color:'#F0F2F5', marginTop:'-4px' },
  queueDesc: { fontSize:'13px', color:'#555', lineHeight:'1.6' },
  mapGrid: { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'12px' },
  mapCard: { border:'1px solid #1e1e1e', borderRadius:'6px', overflow:'hidden', cursor:'pointer', transition:'all 0.2s', background:'#0a0a0a' },
  mapCardImg: { position:'relative', width:'100%', paddingBottom:'55%', overflow:'hidden', background:'#111111' },
  mapImg: { position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover' },
  mapSelected: { position:'absolute', top:'8px', right:'8px', background:'#FF6B2B', borderRadius:'50%', width:'22px', height:'22px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'700', color:'#fff' },
  mapCardName: { padding:'10px 12px', fontFamily:"'Bebas Neue', sans-serif", fontSize:'13px', letterSpacing:'2px', textAlign:'center' },
  queueBtn: { background:'#E8192C', color:'#fff', border:'none', borderRadius:'4px', padding:'16px', fontSize:'15px', fontWeight:'700', letterSpacing:'4px', fontFamily:"'Bebas Neue', sans-serif", cursor:'pointer', clipPath:'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' },
  error: { background:'#1a0508', border:'1px solid #E8192C', borderRadius:'4px', padding:'10px 14px', color:'#E8192C', fontSize:'12px' },

  // IN QUEUE
  inQueueWrap: { display:'flex', flexDirection:'column', alignItems:'center', gap:'20px', padding:'80px 20px', position:'relative' },
  inQueueGlow: { position:'absolute', top:'20%', left:'50%', transform:'translateX(-50%)', width:'400px', height:'200px', background:'radial-gradient(ellipse, rgba(232,25,44,0.1) 0%, transparent 70%)', pointerEvents:'none' },
  inQueueEyebrow: { display:'flex', alignItems:'center', gap:'8px', fontSize:'10px', letterSpacing:'4px', color:'#4CAF50', fontWeight:'700' },
  inQueueTimer: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'80px', letterSpacing:'12px', color:'#E8192C', lineHeight:'1' },
  inQueueLabel: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'20px', letterSpacing:'6px', color:'#333' },
  inQueueMode: { fontSize:'13px', color:'#444', letterSpacing:'1px' },
  dots: { display:'flex', gap:'10px', marginTop:'8px' },
  dot: { width:'8px', height:'8px', borderRadius:'50%', background:'#E8192C', animation:'dotPulse 1.2s infinite' },
  leaveBtn: { background:'transparent', border:'1px solid #E8192C30', borderRadius:'4px', padding:'12px 28px', color:'#E8192C', fontSize:'12px', letterSpacing:'3px', cursor:'pointer', fontFamily:"'Bebas Neue', sans-serif", marginTop:'16px' },

  // MATCH FOUND
  matchFoundBox: { background:'#111111', border:'2px solid #E8192C', borderRadius:'8px', padding:'56px 48px', textAlign:'center', display:'flex', flexDirection:'column', gap:'20px', minWidth:'360px', position:'relative', overflow:'hidden' },
  matchFoundGlow: { position:'absolute', top:'-40px', left:'50%', transform:'translateX(-50%)', width:'300px', height:'200px', background:'radial-gradient(ellipse, rgba(232,25,44,0.2) 0%, transparent 70%)', pointerEvents:'none' },
  matchFoundEyebrow: { display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontSize:'10px', letterSpacing:'4px', color:'#E8192C', fontWeight:'700', position:'relative', zIndex:1 },
  matchFoundTitle: { fontFamily:"'Bebas Neue', sans-serif", fontSize:'48px', letterSpacing:'6px', color:'#F0F2F5', position:'relative', zIndex:1 },
  matchFoundMap: { fontSize:'13px', color:'#555', letterSpacing:'2px', position:'relative', zIndex:1 },
  matchFoundButtons: { display:'flex', gap:'12px', justifyContent:'center', position:'relative', zIndex:1 },
  acceptBtn: { background:'#E8192C', color:'#fff', border:'none', borderRadius:'4px', padding:'14px 36px', fontSize:'15px', letterSpacing:'3px', fontFamily:"'Bebas Neue', sans-serif", cursor:'pointer', clipPath:'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' },
  declineBtn: { background:'transparent', border:'1px solid #1e1e1e', borderRadius:'4px', padding:'14px 28px', color:'#444', fontSize:'13px', letterSpacing:'2px', fontFamily:"'Bebas Neue', sans-serif", cursor:'pointer' },
};